import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import { AccountTree, Save, Cancel, Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CpdActionButton from '@/components/ui/CpdActionButton';
import CpdAutocomplete from '@/components/ui/CpdAutocomplete';
import { useGetFacilities } from '@/api/hooks/facilities/useGetFacilities';

const AssignFacilitiesDialog = ({
  open,
  onClose,
  onSave,
  loading = false,
  facilityGroup,
  organizationId,
}) => {
  const { t } = useTranslation();
  const [selectedFacilityIds, setSelectedFacilityIds] = useState([]);

  const {
    facilities,
    loading: facilitiesLoading,
    error: facilitiesError,
  } = useGetFacilities({
    organizationId,
    enabled: open && !!organizationId,
  });

  useEffect(() => {
    if (open && facilityGroup && facilities.length > 0) {
      const assignedIds = facilities
        .filter((f) => f.facility_group_id === facilityGroup.id)
        .map((f) => f.id);
      setSelectedFacilityIds(assignedIds);
    } else if (!open) {
      setSelectedFacilityIds([]);
    }
  }, [open, facilityGroup, facilities]);

  const facilitiesWithGroupInfo = useMemo(() => {
    return facilities.map((f) => ({
      ...f,
      currentGroupId: f.facility_group_id,
      isReassignment:
        f.facility_group_id && f.facility_group_id !== facilityGroup?.id,
    }));
  }, [facilities, facilityGroup]);

  const selectedFacilities = useMemo(() => {
    return facilitiesWithGroupInfo.filter((f) =>
      selectedFacilityIds.includes(f.id),
    );
  }, [facilitiesWithGroupInfo, selectedFacilityIds]);

  const hasReassignments = selectedFacilities.some((f) => f.isReassignment);

  const handleChange = (event, newValue) => {
    setSelectedFacilityIds(newValue.map((f) => f.id));
  };

  const handleSave = () => {
    onSave(selectedFacilityIds);
  };

  const handleSelectAll = () => {
    setSelectedFacilityIds(facilitiesWithGroupInfo.map((f) => f.id));
  };

  const handleClearAll = () => {
    setSelectedFacilityIds([]);
  };

  const renderOption = (props, facility) => (
    <Box component='li' {...props}>
      <Box sx={{ flex: 1 }}>
        <Typography variant='body2'>{facility.name}</Typography>
        {facility.currentGroupId && (
          <Typography variant='caption' color='warning.main'>
            {facility.currentGroupId === facilityGroup?.id
              ? t('facilityGroups.assign.currentlyAssigned')
              : t('facilityGroups.assign.willReassign')}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderTags = (tagValue, getTagProps) =>
    tagValue.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      return (
        <Chip
          key={key}
          variant='outlined'
          label={option.name}
          color={option.isReassignment ? 'warning' : 'primary'}
          size='small'
          {...tagProps}
        />
      );
    });

  if (!facilityGroup) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTree color='primary' />
          {t('facilityGroups.assign.title', { name: facilityGroup.name })}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {hasReassignments && (
            <Alert severity='warning' icon={<Info />}>
              {t('facilityGroups.assign.reassignWarning')}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size='small'
              variant='outlined'
              onClick={handleSelectAll}
              disabled={
                selectedFacilityIds.length === facilitiesWithGroupInfo.length ||
                loading
              }>
              {t('facilityGroups.assign.selectAll', {
                count: facilitiesWithGroupInfo.length,
              })}
            </Button>
            <Button
              size='small'
              variant='outlined'
              onClick={handleClearAll}
              disabled={selectedFacilityIds.length === 0 || loading}>
              {t('facilityGroups.assign.clearAll')}
            </Button>
          </Box>

          <CpdAutocomplete
            multiple
            options={facilitiesWithGroupInfo}
            value={selectedFacilities}
            onChange={handleChange}
            label={t('facilityGroups.assign.selectFacilities')}
            placeholder={t('facilityGroups.assign.searchPlaceholder')}
            loading={facilitiesLoading}
            apiError={facilitiesError}
            disabled={loading}
            getOptionLabel={(f) => f.name}
            renderOption={renderOption}
            renderTags={renderTags}
          />

          <Typography variant='body2' color='text.secondary'>
            {t('facilityGroups.assign.selectedCount', {
              count: selectedFacilityIds.length,
            })}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button onClick={onClose} startIcon={<Cancel />}>
          {t('common.buttons.cancel')}
        </Button>
        <CpdActionButton
          onClick={handleSave}
          loading={loading}
          startIcon={<Save />}>
          {t('common.buttons.save')}
        </CpdActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default AssignFacilitiesDialog;
